import type { ComponentProps, ReactNode } from 'react'
import clsx from 'clsx'
import { rem } from 'polished'
import styled from 'styled-components'
import Drop from 'LuxKit/Drop'
import SPContentBase, { SPContentBaseAction, type SPContentBaseActionButtonProps } from 'components/Common/SPContentBase/SPContentBase'
import { mediaQueryUp } from 'components/utils/breakpoint'

export type SupportBaseScreen = 'lg' | 'md' | 'sm'

const DROP_SIZE_LG = 180
const DROP_SIZE_WIDE = 406

const Root = styled.div`
  display: grid;
  column-gap: ${rem(20)};
  align-items: center;

  /* CSS-driven layout: mobile-first sm, tablet md, desktop lg. */
  &.screen-responsive {
    grid-template-columns: minmax(0, 2fr) minmax(0, 1fr);
    align-items: stretch;
    width: 100%;
    padding-block: ${rem(8)};
    border-radius: ${props => props.theme.borderRadius.M};
  }

  &.screen-responsive.has-action {
    grid-template-areas:
      "content media"
      "action action";
    row-gap: ${rem(20)};
  }

  ${mediaQueryUp.tablet} {
    &.screen-responsive,
    &.screen-responsive.has-action {
      grid-template-columns: repeat(4, minmax(0, 1fr));
      grid-template-areas: "content content content media";
      row-gap: 0;
      padding-block: 0;
    }
  }

  /* Global Kit Screen=Lg: 4-col, 180px drop, inline CTA. */
  &.screen-lg {
    grid-template-columns: repeat(4, minmax(0, 1fr));
    align-items: stretch;
    width: 100%;
    max-width: ${rem(960)};
    margin-inline: auto;
    border-radius: ${props => props.theme.borderRadius.M};
  }

  /* Global Kit Screen=Md: 4-col, copy spans 3, drop fills 1. */
  &.screen-md {
    grid-template-columns: repeat(4, minmax(0, 1fr));
    align-items: stretch;
    border-radius: ${props => props.theme.borderRadius.M};
  }

  /* Global Kit Screen=Sm: 2fr content + 1fr drop; CTA row only when present. */
  &.screen-sm {
    grid-template-columns: minmax(0, 2fr) minmax(0, 1fr);
    align-items: stretch;
    padding-block: ${rem(8)};
    border-radius: ${props => props.theme.borderRadius.M};
  }

  &.screen-sm.has-action {
    grid-template-areas:
      "content media"
      "action action";
    row-gap: ${rem(20)};
  }
`

const Content = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: start;
  min-width: 0;
  padding-block: ${rem(20)};

  ${Root}.screen-responsive & {
    align-self: stretch;
    padding-block: 0;
    gap: ${rem(16)};
  }

  ${Root}.screen-responsive.has-action & {
    grid-area: content;
  }

  ${mediaQueryUp.tablet} {
    ${Root}.screen-responsive &,
    ${Root}.screen-responsive.has-action & {
      grid-area: content;
      align-self: stretch;
      padding-block: ${rem(8)};
    }
  }

  ${mediaQueryUp.desktop} {
    ${Root}.screen-responsive & {
      padding-block: ${rem(20)};
    }
  }

  ${Root}.screen-lg &,
  ${Root}.screen-md & {
    grid-column: 1 / span 3;
    align-self: stretch;
  }

  ${Root}.screen-md & {
    padding-block: ${rem(8)};
  }

  ${Root}.screen-sm & {
    align-self: stretch;
    padding-block: 0;
  }

  ${Root}.screen-sm.has-action & {
    grid-area: content;
  }
`

const Media = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-width: 0;

  ${Root}.screen-responsive & {
    align-self: start;
    overflow: hidden;
    width: 100%;
  }

  ${Root}.screen-responsive.has-action & {
    grid-area: media;
  }

  ${mediaQueryUp.tablet} {
    ${Root}.screen-responsive &,
    ${Root}.screen-responsive.has-action & {
      grid-area: media;
      width: 100%;
    }
  }

  ${Root}.screen-lg &,
  ${Root}.screen-md & {
    grid-column: 4;
    align-self: start;
  }

  ${Root}.screen-lg & {
    overflow: hidden;
  }

  ${Root}.screen-md &,
  ${Root}.screen-sm & {
    width: 100%;
  }

  ${Root}.screen-sm & {
    align-self: start;
    overflow: hidden;
  }

  ${Root}.screen-sm.has-action & {
    grid-area: media;
  }
`

const SupportDrop = styled(Drop)`
  ${Root}.screen-responsive &,
  ${Root}.screen-md &,
  ${Root}.screen-sm & {
    width: 100%;
  }
`

const InlineCta = styled.div`
  ${Root}.screen-responsive & {
    display: none;
  }

  ${mediaQueryUp.tablet} {
    ${Root}.screen-responsive & {
      display: block;
    }
  }
`

const FullWidthAction = styled.div`
  grid-area: action;
  min-width: 0;

  ${mediaQueryUp.tablet} {
    ${Root}.screen-responsive & {
      display: none;
    }
  }
`

interface Props extends Utils.PropsWithDataAttributes {
  className?: string
  heading: ComponentProps<typeof SPContentBase>['heading']
  body?: ComponentProps<typeof SPContentBase>['body']
  headingAs?: ComponentProps<typeof SPContentBase>['headingAs']
  /**
   * Global Kit Screen variant. Pass `responsive` to layout with CSS media queries.
   * @default lg
   */
  screen?: SupportBaseScreen | 'responsive'
  /**
   * Optional CTA label matching Global Kit Button label. Omit to hide the button.
   */
  buttonLabel?: ComponentProps<typeof SPContentBase>['buttonLabel']
  /**
   * Small-screen full-width CTA. Falls back to `buttonLabel` when omitted.
   */
  compactButtonLabel?: ReactNode
  actionButtonProps?: SPContentBaseActionButtonProps
  /**
   * LuxKit Drop media. `position` defaults to `bottom-right` to match Global Kit.
   */
  drop: Pick<ComponentProps<typeof Drop>, 'id' | 'image' | 'alt' | 'loading' | 'position'>
}

function SupportBase(props: Props) {
  const {
    className,
    heading,
    body,
    headingAs,
    screen = 'lg',
    buttonLabel,
    compactButtonLabel,
    actionButtonProps,
    drop,
    ...rest
  } = props

  const isResponsive = screen === 'responsive'
  const isSm = screen === 'sm'
  const compactLabel = compactButtonLabel ?? buttonLabel
  const hasAction = Boolean(buttonLabel || compactButtonLabel)
  const dropSize = screen === 'lg' ? DROP_SIZE_LG : DROP_SIZE_WIDE
  const showLockedInlineCta = Boolean(buttonLabel) && !isSm && !isResponsive
  const showResponsiveInlineCta = Boolean(buttonLabel) && isResponsive
  const showFullWidthCta = Boolean(compactLabel) && (isSm || isResponsive)
  const inlineActionButtonProps = showLockedInlineCta ? actionButtonProps : undefined

  return <Root
    {...rest}
    className={clsx(
      className,
      `screen-${screen}`,
      (isSm || isResponsive) && hasAction && 'has-action',
    )}
  >
    <Content>
      <SPContentBase
        heading={heading}
        body={body}
        headingAs={headingAs}
        buttonLabel={showLockedInlineCta ? buttonLabel : undefined}
        actionButtonProps={inlineActionButtonProps}
      />
      {showResponsiveInlineCta && <InlineCta>
        <SPContentBaseAction {...actionButtonProps}>
          {buttonLabel}
        </SPContentBaseAction>
      </InlineCta>}
    </Content>
    <Media>
      <SupportDrop
        id={drop.id}
        image={drop.image}
        alt={drop.alt}
        loading={drop.loading}
        position={drop.position ?? 'bottom-right'}
        size={dropSize}
      />
    </Media>
    {showFullWidthCta && <FullWidthAction>
      <SPContentBaseAction
        {...actionButtonProps}
        fit="full-width"
      >
        {compactLabel}
      </SPContentBaseAction>
    </FullWidthAction>}
  </Root>
}

export default SupportBase
